import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Check if user needs to change password and fetch profile data
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('must_change_password, username')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user && isAuthenticated
  });

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the intended destination for post-login redirect
    if (location.pathname !== '/auth') {
      localStorage.setItem('auth_redirect_path', location.pathname);
    }
    return <Navigate to="/auth" replace />;
  }

  // If user must change password and not already on change-password page
  if (profile?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // If user doesn't have a profile and not on create profile page, redirect to create
  if (!profile && location.pathname !== '/create-myspai-profile') {
    return <Navigate to="/create-myspai-profile" replace />;
  }

  // If user has a profile and is trying to access create profile page, redirect to their profile
  if (profile?.username && location.pathname === '/create-myspai-profile') {
    return <Navigate to={`/myspai-profile/${profile.username}`} replace />;
  }

  return <>{children}</>;
};
